import React from 'react'
import styled from 'styled-components'
import { Flex, Image, P, ResponsiveSVG } from 'ui/acro'
import { Text } from 'ui/bend'
import { AlomovesIconSVG } from 'images/latest'
import AccessIdentifier from '../../../components_v1/SchemaSectionRenderer/common/components/AccessIdentifier'
import { showLabel } from '../../../components_v1/SchemaSectionRenderer/common/components/utils'
import { LIKED_ITEMS_SLUG } from 'components_v1/common'
import moment from 'moment'
import CommonLink from 'components_v2/CommonLink'

const Title = styled.div`
  color: ${(props) => props.theme.colors.black};
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 18px;
  font-weight: 400;
  line-height: 24px; /* 133.333% */
  letter-spacing: 0.396px;
  width: 80%;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover,
  :visited,
  :active {
    color: ${(props) => props.theme.colors.black};
  }
`
const AuthorName = styled(Text)`
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  text-transform: capitalize;
  color: ${(props) => props.theme.colors.black};
`
const DurationText = styled(Text)`
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  text-transform: capitalize;
  color: ${(props) => props.theme.colors.black};
  @media screen and (max-width: 760px) {
    text-align: right;
  }
`
const CompletedText = styled(Text)`
  color: ${(props) => props.theme.colors.success.medium};
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
`
const DurationDesktop = styled(Text)`
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 16.1px */
  color: ${(props) => props.theme.colors.black};
  @media screen and (max-width: 760px) {
    display: none;
  }
`

const DurationMobile = styled(Text)`
  font-family: ${(props) => props.theme.fonts.Proxima};
  display: none;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 16.1px */
  color: ${(props) => props.theme.colors.black};
  @media screen and (max-width: 760px) {
    display: block;
  }
`

const Duration = styled(Text)`
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 16.1px */
  color: ${(props) => props.theme.colors.black};
`

const DurationWrapper = styled(Flex)`
  align-items: flex-end;
  flex-grow: 1;
  @media screen and (max-width: 760px) {
    justify-content: space-between;
  }
`

const getLocalDate = (d) => {
  let date = new Date(d)
  if (moment(date).isValid()) {
    return moment(date).local().format('MM/DD/YYYY')
  }

  date = String(d).replace(/-/g, '/')
  if (moment(date).isValid()) {
    return moment(date).local().format('MM/DD/YYYY')
  }

  return String(d)
}

const PlaylistClass = ({
  classObject,
  uuid,
  setIsOpen,
  setSelectedClassObject,
  isGridView,
  signedIn,
  membershipTier
}) => {
  const {
    type,
    videoThumbnail,
    title,
    humanDuration,
    completedAt,
    planEntryUrl,
    availabilityGating,
    coverPhoto,
    workoutCount,
    slug
  } = classObject
  const planData =
    classObject?.type === 'SERIES' ? classObject : classObject?.plan
  const instructorName = planData?.coaches?.map(
    (coach) => `${coach.firstName} ${coach.lastName}`
  )[0]

  const cardThumbnail = type === 'SERIES' ? coverPhoto?.url : videoThumbnail

  const cardUrl =
    classObject?.type === 'SERIES' ? `/series/${slug}` : planEntryUrl

  return (
    <Flex gap='8px'>
      <Flex
        flex={['0 0 30%', '0 0 30%', '0 0 20%', '0 0 20%']}
        as={CommonLink}
        to={cardUrl}
        style={{ position: 'relative' }}
      >
        {cardThumbnail ? (
          <Image
            src={cardThumbnail}
            objectFit='cover'
            position='absolute'
            top='0'
            left='0'
            style={{ aspectRatio: '16/9' }}
          />
        ) : (
          <ResponsiveSVG
            SVG={AlomovesIconSVG}
            className='no-thumbnail'
            style={{ aspectRatio: '16/9' }}
          />
        )}

        {showLabel(membershipTier, availabilityGating) && (
          <AccessIdentifier
            availabilityGating={availabilityGating}
            isGridView={isGridView}
            rightPosition={'3px'}
            bottomPosition={'3px'}
          />
        )}
      </Flex>
      <Flex
        flex={['0 0 68%', '0 0 68%', '0 0 78%', '0 0 78%']}
        flexDirection='column'
        style={{ minWidth: '0' }}
      >
        <Flex
          justifyContent='space-between'
          alignItems='center'
          style={{ minWidth: '0' }}
        >
          {title && (
            <Title style={{ minWidth: '0' }} as={CommonLink} to={cardUrl}>
              {`${title} `}
            </Title>
          )}
          {signedIn && (
            <P
              style={{ color: '#000', cursor: 'pointer', margin: '0' }}
              onClick={() => {
                setIsOpen(true)
                setSelectedClassObject(classObject)
              }}
            >
              • • •
            </P>
          )}
        </Flex>
        <AuthorName>{instructorName}</AuthorName>
        {humanDuration && <DurationDesktop>{humanDuration}</DurationDesktop>}
        <DurationWrapper alignItems='flex-end' flexGrow='1' gap='32px'>
          {completedAt && (
            <CompletedText>
              Completed: {getLocalDate(completedAt)}
            </CompletedText>
          )}

          {humanDuration && <DurationMobile>{humanDuration}</DurationMobile>}
        </DurationWrapper>
        {uuid === LIKED_ITEMS_SLUG && type === 'SERIES' && (
          <DurationText>
            <Duration>{workoutCount}</Duration> Classes
          </DurationText>
        )}
      </Flex>
    </Flex>
  )
}
PlaylistClass.propTypes = {}

export default PlaylistClass
